/**
 * Plugin to handle removing list formatting with backspace
 * Similar to Google Docs behavior where pressing backspace on empty list item removes list formatting
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $isListItemNode, 
  $isListNode,
  REMOVE_LIST_COMMAND 
} from '@lexical/list';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

export default function ListRemovePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => {
        const selection = $getSelection();
        
        if (!$isRangeSelection(selection)) {
          return false;
        }

        // Only proceed if selection is collapsed (cursor, not selection range)
        if (!selection.isCollapsed()) {
          return false;
        }

        const anchor = selection.anchor;
        
        // Only trigger at the start of the node (offset 0)
        if (anchor.offset !== 0) {
          return false;
        }

        const anchorNode = anchor.getNode();
        const element = anchorNode.getParent();

        // Check if we're in a list item
        if (!$isListItemNode(element)) {
          return false;
        }

        // Check if the list item is empty
        const textContent = element.getTextContent();
        if (textContent.length > 0) {
          return false;
        }

        // Get the parent list
        const list = element.getParent();
        if (!$isListNode(list)) {
          return false;
        }

        // Remove list formatting
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  return null;
}
